import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";

export const EASE = [0.22, 1, 0.36, 1];

const LOGO_URL = "/logo.png";

export const Logo = ({ className = "h-9 sm:h-10 w-auto" }) => (
  <img loading="lazy" decoding="async" src={LOGO_URL} alt="Verde Capital" className={className} data-testid="logo" />
);

export const Eyebrow = ({ children, light = false, className = "" }) => (
  <p className={`eyebrow ${light ? "eyebrow-light" : ""} ${className}`}>{children}</p>
);

export const ArrowCta = ({ to, children, variant = "solid", onClick, className = "", testid }) => {
  const base =
    "group inline-flex items-center justify-center gap-2.5 rounded-full px-6 py-3 text-sm font-semibold transition-all duration-300";
  const styles = {
    solid: "bg-verde-800 text-white hover:bg-verde-700",
    ghost: "border border-sand bg-white text-charcoal hover:border-verde-700",
    light: "bg-white text-verde-900 hover:bg-cream",
    "ghost-light": "border border-white/25 text-white hover:bg-white hover:text-verde-900",
  };
  const inner = (
    <>
      {children}
      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
    </>
  );
  if (to) {
    return (
      <Link to={to} data-testid={testid} onClick={onClick} className={`${base} ${styles[variant]} ${className}`}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" data-testid={testid} onClick={onClick} className={`${base} ${styles[variant]} ${className}`}>
      {inner}
    </button>
  );
};

export const Reveal = ({ children, delay = 0, y = 24, className = "" }) => (
  <div
    className={className}
    style={{
      opacity: 0,
      transform: `translateY(${y}px)`,
      transition: `opacity 0.9s cubic-bezier(0.22,1,0.36,1) ${delay}s, transform 0.9s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
    }}
    ref={(el) => {
      if (!el || el.dataset.revealed) return;
      const io = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            el.dataset.revealed = "1";
            el.style.opacity = "1";
            el.style.transform = "translateY(0)";
            io.disconnect();
          }
        },
        { threshold: 0.12 }
      );
      io.observe(el);
    }}
  >
    {children}
  </div>
);

export const MaskLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden pt-[0.08em] pb-[0.16em] -mt-[0.08em] -mb-[0.16em] ${className}`}>
    <MotionSpan delay={delay}>{children}</MotionSpan>
  </span>
);

const MotionSpan = ({ children, delay }) => {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setOn(true), 60);
    return () => clearTimeout(t);
  }, []);
  return (
    <span
      ref={ref}
      className="block will-change-transform"
      style={{
        transform: on ? "translateY(0%)" : "translateY(110%)",
        transition: `transform 1.1s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
      }}
    >
      {children}
    </span>
  );
};

export const MaskLineView = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <span ref={ref} className={`block overflow-hidden pt-[0.08em] pb-[0.16em] -mt-[0.08em] -mb-[0.16em] ${className}`}>
      <span
        className="block will-change-transform"
        style={{
          transform: shown ? "translateY(0%)" : "translateY(110%)",
          transition: `transform 1.1s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
        }}
      >
        {children}
      </span>
    </span>
  );
};

export const WhatsAppIcon = ({ className = "h-4 w-4" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export const VerdeSpark = ({ className = "h-4 w-4" }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <path d="M12 21.5v-7.3" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
    <path d="M12 14.2c.2-3.8 2.6-6.2 6-6.7-.2 3.8-2.6 6.2-6 6.7z" fill="currentColor" />
    <path d="M12 17.4c-.2-3.3-2.3-5.3-5.4-5.8.2 3.3 2.3 5.3 5.4 5.8z" fill="currentColor" opacity="0.5" />
    <path d="M19.3 2.3l.7 1.9 1.9.7-1.9.7-.7 1.9-.7-1.9-1.9-.7 1.9-.7.7-1.9z" fill="#C8A468" />
  </svg>
);

export const CheckItem = ({ children, light = false }) => (
  <li className="flex items-start gap-3">
    <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${light ? "bg-mint/25" : "bg-verde-50"}`}>
      <Check className={`h-3 w-3 ${light ? "text-mint" : "text-verde-700"}`} strokeWidth={3} />
    </span>
    <span className={`text-[15px] leading-relaxed ${light ? "text-cream/85" : "text-ink"}`}>{children}</span>
  </li>
);

export const Faq = ({ items }) => (
  <div className="space-y-3">
    {items.map((item, i) => (
      <details key={i} className="group card overflow-hidden px-6" data-testid={`faq-item-${i}`}>
        <summary className="flex cursor-pointer items-center justify-between gap-6 py-5 list-none [&::-webkit-details-marker]:hidden">
          <span className="font-display text-lg font-semibold text-charcoal">{item.q}</span>
          <span className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-sand text-verde-800 transition-transform duration-300 group-open:rotate-45">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.6" />
            </svg>
          </span>
        </summary>
        <p className="pb-6 pr-6 text-[15px] text-ink leading-relaxed">{item.a}</p>
      </details>
    ))}
  </div>
);
